import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/rootReducer';
import { AppThunk } from 'app/store';
import { useSelector } from 'react-redux';
import { ErrorResult, getErrorResult } from 'models/error';
import { Ship } from 'models/ships';
import { doFilterShips } from 'utils/misc';
import { getQSReportUrlAsync } from 'api/maricoApiQSReport';

/**
 * QSReportState インターフェース
 */
interface QSReportState {
  /** 船舶リスト */
  ships: Ship[] | undefined;
  /** フィルター済み船舶リスト */
  filteredShips: Ship[];
  /** フィルター船舶名 */
  filterShipName: string;
  /** 選択中の船舶ID */
  selectedShipId: string | undefined;
  /** 埋め込みURL */
  embedUrl: string | undefined;
  /** データ取得中状態 */
  fetching: boolean;
  /** データ取得エラー */
  fetchError: ErrorResult | undefined;
}

/**
 * QSReport の初期状態
 */
const initialState: QSReportState = {
  ships: undefined,
  filteredShips: [],
  filterShipName: '',
  selectedShipId: undefined,
  embedUrl: undefined,
  fetching: false,
  fetchError: undefined,
};

export const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    /**
     * 船舶リストを設定する。
     */
    setShips: (state, { payload }: PayloadAction<Ship[]>) => {
      const _ships = payload;
      const ships = _ships.map((ship) => {
        return {
          shipId: ship.shipId,
          name: ship.name,
          propulsionSerialNumbers: ship.propulsionSerialNumbers,
          machines: ship.machines.map((machine) => {
            return {
              machineId: machine.machineId,
              name: machine.name,
              serialNumbers: machine.serialNumbers,
              dataFormatId: machine.dataFormatId,
              sensorGroups: machine.sensorGroups.filter((x) =>
                x.sensors.find(
                  (y) => y.isSearch && y.displayLowerLimit != null && y.displayUpperLimit != null
                )
              ),
              checked: false,
            };
          }),
          hasQSDashboard: ship.hasQSDashboard,
          createdAt: ship.createdAt,
          updatedAt: ship.updatedAt,
          checked: false,
          propulsionSerialNumbersText: ship.propulsionSerialNumbers.join(', '),
          machineNamesText: ship.machines.map((x) => x.name).join(', '),
        };
      });

      ships.forEach((ship) => {
        ship.machines.forEach((machine) => {
          machine.checked = false;
        });
        ship.checked = ship.machines.every((x) => x.checked);
      });

      state.ships = ships;
      state.filteredShips = doFilterShips(state.ships, state.filterShipName, true);
    },
    /**
     * フィルター船舶名を設定する。
     */
    setFilterShipName: (state, action: PayloadAction<string>) => {
      state.filterShipName = action.payload;
      if (state.ships != null) {
        state.filteredShips = doFilterShips(state.ships, state.filterShipName, true);
      }
    },
    /**
     * 選択中の船舶IDを設定する。
     */
    setSelectedShipId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedShipId = action.payload;
    },
    /**
     * 船舶リストをクリアする。
     */
    clearShips: (state) => {
      state.ships = undefined;
      state.filterShipName = '';
      state.filteredShips = [];
      state.selectedShipId = undefined;
      state.embedUrl = undefined;
    },
    /**
     * 取得の開始を設定する。
     */
    startFetch: (state, action: PayloadAction<boolean>) => {
      state.fetching = action.payload;
    },
    /**
     * 埋め込みURLを設定する。
     */
    setEmbedUrl: (state, { payload }: PayloadAction<string>) => {
      state.embedUrl = payload;
      state.fetching = false;
    },
    /**
     * 取得エラーを設定する。
     */
    setFetchError: (state, action: PayloadAction<ErrorResult | undefined>) => {
      state.fetchError = action.payload;
      state.fetching = false;
    },
    /**
     * QSReportStateをクリアする。
     */
    clearQSReportState: (state) => {
      state.ships = initialState.ships;
      state.filteredShips = initialState.filteredShips;
      state.filterShipName = initialState.filterShipName;
      state.selectedShipId = initialState.selectedShipId;
      state.embedUrl = initialState.embedUrl;
      state.fetching = initialState.fetching;
      state.fetchError = initialState.fetchError;
    },
  },
});

export const {
  setShips,
  setFilterShipName,
  setSelectedShipId,
  clearShips,
  setEmbedUrl,
  setFetchError,
  clearQSReportState,
} = reportSlice.actions;

/**
 * 埋め込みURLを取得する。
 */
export const fetchEmbedUrl =
  (ship: Ship): AppThunk =>
  async (dispatch) => {
    dispatch(reportSlice.actions.startFetch(true));
    try {
      const embedUrl = await getQSReportUrlAsync(ship.shipId);
      dispatch(reportSlice.actions.setEmbedUrl(embedUrl));
    } catch (error) {
      dispatch(reportSlice.actions.setFetchError(getErrorResult(error)));
    }
  };

const qsReportState = (state: RootState) => state.qsReport;
const selectFilteredShips = createSelector(qsReportState, (x) => x.filteredShips);
const selectFilterShipName = createSelector(qsReportState, (x) => x.filterShipName);
const selectSelectedShipId = createSelector(qsReportState, (x) => x.selectedShipId);
const selectFetching = createSelector(qsReportState, (x) => x.fetching);
const selectFetchError = createSelector(qsReportState, (x) => x.fetchError);
const selectEmbedUrl = createSelector(qsReportState, (x) => x.embedUrl);

export const useQSReportFilteredShips = () => useSelector(selectFilteredShips);
export const useQSReportFilterShipName = () => useSelector(selectFilterShipName);
export const useQSReportSelectedShipId = () => useSelector(selectSelectedShipId);
export const useQSReportFetching = () => useSelector(selectFetching);
export const useQSReportFetchError = () => useSelector(selectFetchError);
export const useQSReportEmbedUrl = () => useSelector(selectEmbedUrl);

export default reportSlice.reducer;
